import {
    DirectionalLight,
    HemisphereLight,
    Object3D,
    Raycaster,
    Scene,
    Vector3,
} from "three";
import { GPUParticleSystem } from "./GPUParticleSystem/GPUParticleSystem";
import Ground from "./Ground";
import { IGameObject } from "./IGameObject";
import Loader from "./Loader";
import Tent from "./SceneObjects/Tent";
import OtherPlayer from "./Player/OtherPlayer";
import OnlineObjects, { IOnlineObject } from "./OnlineObjects";
import {
    WORLD_SIZE,
    DAY_LENGTH,
    START_TIME,
    DAY_SKY,
    NIGHT_SKY,
    DAY_ATMOSPHERE,
    NIGHT_ATMOSPHERE,
    REFRESH_TICK,
} from "./Constants";

import Flag from "./SceneObjects/Flag";
import { updateFlag } from "./GamePlay";

interface SceneObject {
    onlineObject: IOnlineObject;
    gameObject: IGameObject;
}

class World {
    public worldSize: number;

    public tick: number;

    public objects: IGameObject[];

    public ground: Ground | null = null;

    public particleSystem: GPUParticleSystem;

    public fogNear: number;

    public fogFar: number;

    public timeTicks: number;

    public atmosphereLight: HemisphereLight;

    public sunLight: DirectionalLight;

    public onlineObjects: OnlineObjects;

    private sceneObjectsByOnlineObjectId: Record<string, SceneObject>;

    private refreshInterval: ReturnType<typeof setInterval>;

    private currentPlayerId: string;

    private loader: Loader;

    constructor(scene: Scene, loader: Loader, currentPlayerId: string) {
        this.worldSize = WORLD_SIZE;
        this.tick = 0;
        this.objects = [];
        this.particleSystem = new GPUParticleSystem({
            maxParticles: 1000,
        });
        this.fogNear = 20;
        this.fogFar = 25;
        this.timeTicks = START_TIME;

        this.atmosphereLight = new HemisphereLight(
            DAY_ATMOSPHERE,
            0xccffcc,
            0.6
        );
        this.sunLight = new DirectionalLight(0xffffff, 1);

        this.onlineObjects = new OnlineObjects();

        this.currentPlayerId = currentPlayerId;

        this.sceneObjectsByOnlineObjectId = {};

        this.createLevel(scene, loader);

        this.loader = loader;

        this.refreshInterval = setInterval(
            () => this.refresh(scene, this.onlineObjects),
            REFRESH_TICK
        );
    }

    public async createLevel(scene: Scene, loader: Loader): Promise<void> {
        const { sunLight, atmosphereLight } = this;

        // World Lights
        atmosphereLight.position.set(0, 9, 0);
        scene.add(atmosphereLight);

        sunLight.position.set(10, 20, 0);
        const target = new Object3D();
        scene.add(target);
        target.position.set(100, 0, 0);
        sunLight.target = target;
        scene.add(sunLight);

        // Particles
        scene.add(this.particleSystem);

        // World objects
        this.ground = new Ground(scene, this.worldSize);

        const tent = new Tent(scene, loader);
        tent.position.y = 30;
        tent.position.y = this.getGroundIntersection(tent.position, 0);

        this.createTrees(scene, loader);
    }

    public createTrees(scene: Scene, loader: Loader): void {
        loader.loadModel(
            `${process.env.ASSET_URL}/models/tree.dae`,
            (tree: Scene) => {
                for (let i = 0; i < 128; i += 1) {
                    this.createTree(scene, tree);
                }
            }
        );
    }

    public createTree(scene: Scene, tree: Scene): void {
        const thisTree = tree.clone();

        const height = 0;
        thisTree.position.y = 30;
        thisTree.position.x = this.randomPosition();
        thisTree.position.z = this.randomPosition();
        thisTree.position.y =
            this.getGroundIntersection(thisTree.position, 0) + height / 2;
        const randomSize = 0.4 + Math.random() / 5;
        thisTree.scale.set(randomSize, randomSize, randomSize);
        thisTree.rotateZ(Math.random() * Math.PI * 2);
        scene.add(thisTree);
    }

    public randomPosition(): number {
        return (Math.random() - 0.5) * this.worldSize;
    }

    public getGroundIntersection(
        origin: Vector3,
        fallbackValue: number
    ): number {
        const groundRaycaster = new Raycaster();
        groundRaycaster.set(origin, new Vector3(0, -1, 0));
        const intersects = groundRaycaster.intersectObjects(
            this.ground !== null ? [this.ground.mesh] : []
        );

        if (intersects.length > 0) {
            return intersects[0].point.y;
        }
        return fallbackValue;
    }

    private getDayPercent() {
        return (this.timeTicks % DAY_LENGTH) / DAY_LENGTH;
    }

    static getLightness(dayPercent: number): number {
        return Math.sin(dayPercent * Math.PI * 2);
    }

    public update(delta: number): void {
        const { sunLight, atmosphereLight } = this;

        this.tick += delta / 10;
        if (this.tick < 0) {
            this.tick = 0;
        }
        this.particleSystem.update(this.tick);
        this.objects.forEach((object) => {
            object.update(delta);
        });

        // Time of day
        this.timeTicks += delta;

        const dayPercent = this.getDayPercent();
        const lightness = World.getLightness(dayPercent);

        sunLight.intensity = Math.max(lightness, 0);

        atmosphereLight.color = NIGHT_ATMOSPHERE.clone().lerp(
            DAY_ATMOSPHERE,
            Math.max(lightness, 0)
        );

        // Flag logic
        const redFlag = this.objects.find((x) => x.type === "red-flag");
        const blueFlag = this.objects.find((x) => x.type === "blue-flag");

        const flags = [
            ...(redFlag ? [redFlag] : []),
            ...(blueFlag ? [blueFlag] : []),
        ];

        // get player
        const player = this.objects.find((x) => x.id === this.currentPlayerId);

        // iterate each flag
        if (player) flags.forEach((flag) => updateFlag({ flag, player }));
    }

    public getSkyColour(): number {
        const dayPercent = this.getDayPercent();
        const lightness = World.getLightness(dayPercent);
        return NIGHT_SKY.clone().lerp(DAY_SKY, Math.max(lightness, 0)).getHex();
    }

    public add(object: IGameObject): void {
        this.objects.push(object);
    }

    private async refresh(scene: Scene, onlineObjects: OnlineObjects) {
        onlineObjects.loadObjects();

        const objectDetails = await onlineObjects.getObjects();

        objectDetails.forEach((onlineObject) => {
            // If already in scene, just move
            const existingSceneObject =
                this.sceneObjectsByOnlineObjectId[onlineObject.id];
            if (existingSceneObject) {
                existingSceneObject.gameObject.refresh(onlineObject);

                return;
            }

            // If not in scene, create

            switch (onlineObject.type) {
                case "player": {
                    // don't render current player
                    if (onlineObject.id === this.currentPlayerId) return;

                    const gameObject = new OtherPlayer(
                        scene,
                        onlineObject.id,
                        onlineObject.name
                    );
                    gameObject.refresh(onlineObject);

                    this.objects.push(gameObject);

                    this.sceneObjectsByOnlineObjectId = {
                        ...this.sceneObjectsByOnlineObjectId,
                        [onlineObject.id]: {
                            onlineObject,
                            gameObject,
                        },
                    };
                    break;
                }
                case "red-flag":
                case "blue-flag": {
                    // don't render current player
                    if (onlineObject.id === this.currentPlayerId) return;

                    const colour =
                        onlineObject.type === "red-flag" ? "red" : "blue";

                    const gameObject = new Flag(
                        scene,
                        this.loader,
                        colour,
                        this,
                        onlineObject.id
                    );
                    gameObject.refresh(onlineObject);

                    this.objects.push(gameObject);

                    this.sceneObjectsByOnlineObjectId = {
                        ...this.sceneObjectsByOnlineObjectId,
                        [onlineObject.id]: {
                            onlineObject,
                            gameObject,
                        },
                    };
                    break;
                }
                default: {
                    throw new Error("Object type not recognized");
                    break;
                }
            }
        });
    }
}

export default World;
